import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Home, Compass, CalendarDays, Users2, Luggage, BedDouble, CarFront, UtensilsCrossed, Ticket, MessageSquareText, UserRound, Wallet } from "lucide-react";
import { toast } from "sonner";
import SEO from "../lib/seo";
import { postInquiry } from "../lib/api";
import { useSettings } from "../lib/store";
import { track } from "../lib/track";
import { waLink, MSG } from "../lib/whatsapp";
import { WhatsAppIcon } from "../components/icons";

const STEP_META = [
  { icon: Compass, title: "Destination" },
  { icon: CalendarDays, title: "Travel Date" },
  { icon: Users2, title: "Number of Travelers" },
  { icon: Luggage, title: "Trip Type" },
  { icon: BedDouble, title: "Hotel Preference" },
  { icon: CarFront, title: "Transportation" },
  { icon: UtensilsCrossed, title: "Meal Preference" },
  { icon: Ticket, title: "Attraction" },
  { icon: MessageSquareText, title: "Special Request" },
  { icon: UserRound, title: "Contact Information" },
];

const TRIP_TYPES = ["Private Trip", "Group Trip", "Family Trip", "Honeymoon", "Corporate", "Study Tour", "Halal Trip"];
const HOTELS = ["3 Star", "4 Star", "5 Star", "Fleksibel / Konsultasi"];
const TRANSPORTS = ["Private Van (7-14 seat)", "Private Bus (27-40 seat)", "Semi Private", "Fleksibel / Konsultasi"];
const MEALS = ["Regular", "Halal Food", "Vegetarian", "Other"];
const BUDGETS = ["Di bawah Rp 10 juta / pax", "Rp 10 - 20 juta / pax", "Di atas Rp 20 juta / pax", "Konsultasi dulu"];

const initial = {
  destination: "", arrival: "", departure: "", pax: "", tripType: "", hotel: "",
  transport: "", meal: "", mealOther: "", attraction: "", special: "", budget: "",
  name: "", whatsapp: "", email: "",
};

function OptionCards({ options, value, onSelect, testidPrefix }) {
  return (
    <div className="grid gap-2.5 sm:grid-cols-2">
      {options.map((o) => (
        <button
          type="button"
          key={o}
          onClick={() => onSelect(o)}
          data-testid={`${testidPrefix}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`rounded-2xl border p-4 text-left text-sm font-semibold transition-all duration-200 ${
            value === o
              ? "border-tosca-600 bg-tosca-50 text-tosca-700 ring-2 ring-tosca-600/20"
              : "border-slate-200 bg-white text-charcoal hover:border-tosca-400"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

export default function CustomTrip() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initial);
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const { settings } = useSettings();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setVal = (k) => (v) => setForm({ ...form, [k]: v });

  const valid = () => {
    switch (step) {
      case 0: return !!form.destination.trim();
      case 1: return !!form.arrival && !!form.departure;
      case 2: return !!form.pax;
      case 3: return !!form.tripType;
      case 9: return !!form.name.trim() && !!form.whatsapp.trim();
      default: return true;
    }
  };

  const next = () => {
    if (!valid()) {
      toast.error("Mohon lengkapi data pada langkah ini.");
      return;
    }
    if (step < 9) setStep(step + 1);
    else submit();
  };

  const submit = async () => {
    setSending(true);
    try {
      await postInquiry({
        type: "custom_trip",
        name: form.name,
        whatsapp: form.whatsapp,
        email: form.email,
        destination: form.destination,
        travel_date: form.arrival,
        pax: form.pax,
        trip_type: form.tripType,
        source: "custom-trip-page",
        details: {
          arrival: form.arrival, departure: form.departure, hotel: form.hotel,
          transport: form.transport, meal: form.meal === "Other" ? `Other: ${form.mealOther}` : form.meal,
          attraction: form.attraction, special: form.special, budget: form.budget,
        },
      });
      track("submit_custom_trip", { destination: form.destination });
      setDone(true);
    } catch {
      toast.error("Gagal mengirim permintaan. Silakan coba lagi.");
    } finally {
      setSending(false);
    }
  };

  const summaryMsg = () => {
    const lines = [
      MSG.custom,
      "",
      `Destinasi: ${form.destination || "-"}`,
      `Tanggal: ${form.arrival || "-"} s/d ${form.departure || "-"}`,
      `Jumlah Peserta: ${form.pax || "-"}`,
      `Tipe Trip: ${form.tripType || "-"}`,
      form.hotel ? `Hotel: ${form.hotel}` : "",
      form.meal ? `Makan: ${form.meal}` : "",
    ].filter(Boolean);
    return lines.join("\n");
  };

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream px-6 pb-24 pt-28">
        <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} className="max-w-lg rounded-3xl bg-white p-8 text-center shadow-elevated sm:p-12" data-testid="custom-trip-success">
          <span className="mx-auto inline-flex h-20 w-20 items-center justify-center rounded-full bg-tosca-50">
            <CheckCircle2 className="h-10 w-10 text-tosca-600" />
          </span>
          <h2 className="mt-6 font-heading text-2xl font-extrabold text-charcoal">Permintaan Terkirim</h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">
            Terima kasih. Tim Beyond Travel akan menghubungi Anda melalui WhatsApp untuk membahas kebutuhan perjalanan Anda.
          </p>
          <a href={waLink(settings, summaryMsg())} target="_blank" rel="noreferrer" className="btn btn-wa mt-7 w-full" data-testid="custom-trip-success-whatsapp">
            <WhatsAppIcon className="h-5 w-5" /> Lanjutkan ke WhatsApp
          </a>
          <Link to="/" className="btn btn-outline mt-3 w-full" data-testid="custom-trip-success-home">
            <Home className="h-4 w-4" /> Kembali ke Home
          </Link>
        </motion.div>
      </div>
    );
  }

  const meta = STEP_META[step];

  return (
    <div className="min-h-screen bg-cream pb-24 pt-32">
      <SEO title="Custom Trip — Build Your Own Journey | Beyond Travel" description="Punya itinerary sendiri? Susun custom trip bersama Beyond Travel: destinasi, tanggal, hotel, transport, meal preference, hingga special request." />
      <div className="container-x max-w-2xl">
        <div className="text-center">
          <p className="eyebrow">Custom Trip</p>
          <h1 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-charcoal sm:text-4xl">Build Your Own Journey</h1>
          <p className="mx-auto mt-3 max-w-lg text-sm text-slate-500 sm:text-base">
            Punya itinerary sendiri? Biarkan Beyond Travel membantu menyiapkan kebutuhan perjalanan Anda.
          </p>
        </div>

        <div className="mt-8 rounded-3xl bg-white p-6 shadow-elevated sm:p-10" data-testid="custom-trip-form">
          <div className="flex items-center justify-between">
            <span className="chip bg-tosca-50 text-tosca-700" data-testid="custom-trip-step-indicator">STEP {step + 1} / 10</span>
            <div className="flex flex-1 items-center gap-2 px-4">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-tosca-600 to-gold"
                  animate={{ width: `${((step + 1) / 10) * 100}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>
            </div>
          </div>

          <div className="mt-8">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-tosca-600 text-gold">
                <meta.icon className="h-5 w-5" />
              </span>
              <h2 className="font-heading text-xl font-bold text-charcoal">{meta.title}</h2>
            </div>

            <div className="mt-6">
              {step === 0 && (
                <div>
                  <label className="label">Destinasi*</label>
                  <input className="field" placeholder="cth. China, Japan, Turkey, Europe..." value={form.destination} onChange={set("destination")} data-testid="custom-trip-destination-input" />
                </div>
              )}
              {step === 1 && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="label">Arrival Date*</label>
                    <input type="date" className="field" value={form.arrival} onChange={set("arrival")} data-testid="custom-trip-arrival-input" />
                  </div>
                  <div>
                    <label className="label">Departure Date*</label>
                    <input type="date" className="field" value={form.departure} onChange={set("departure")} data-testid="custom-trip-departure-input" />
                  </div>
                </div>
              )}
              {step === 2 && (
                <div>
                  <label className="label">Number of Travelers*</label>
                  <input className="field" type="number" min="1" placeholder="cth. 4" value={form.pax} onChange={set("pax")} data-testid="custom-trip-pax-input" />
                  <p className="mt-2 text-xs text-slate-400">Termasuk anak-anak dan infan. Bisa ditulis "20 orang + 2 leader" pada special request.</p>
                </div>
              )}
              {step === 3 && <OptionCards options={TRIP_TYPES} value={form.tripType} onSelect={setVal("tripType")} testidPrefix="custom-trip-trip-type" />}
              {step === 4 && <OptionCards options={HOTELS} value={form.hotel} onSelect={setVal("hotel")} testidPrefix="custom-trip-hotel" />}
              {step === 5 && <OptionCards options={TRANSPORTS} value={form.transport} onSelect={setVal("transport")} testidPrefix="custom-trip-transport" />}
              {step === 6 && (
                <div className="space-y-4">
                  <OptionCards options={MEALS} value={form.meal} onSelect={setVal("meal")} testidPrefix="custom-trip-meal" />
                  {form.meal === "Other" && (
                    <input className="field" placeholder="Tuliskan preferensi makan Anda" value={form.mealOther} onChange={set("mealOther")} data-testid="custom-trip-meal-other-input" />
                  )}
                </div>
              )}
              {step === 7 && (
                <div>
                  <label className="label">Attraction</label>
                  <textarea className="field min-h-28" placeholder="Atraksi/objek wisata yang ingin dikunjungi..." value={form.attraction} onChange={set("attraction")} data-testid="custom-trip-attraction-input" />
                </div>
              )}
              {step === 8 && (
                <div className="space-y-4">
                  <div>
                    <label className="label">Special Request</label>
                    <textarea className="field min-h-24" placeholder="Kebutuhan khusus, dsb." value={form.special} onChange={set("special")} data-testid="custom-trip-special-input" />
                  </div>
                  <div>
                    <label className="label"><Wallet className="mr-1 inline h-3.5 w-3.5" />Budget Range</label>
                    <OptionCards options={BUDGETS} value={form.budget} onSelect={setVal("budget")} testidPrefix="custom-trip-budget" />
                  </div>
                </div>
              )}
              {step === 9 && (
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="label">Nama*</label>
                      <input className="field" placeholder="Nama Anda" value={form.name} onChange={set("name")} data-testid="custom-trip-name-input" />
                    </div>
                    <div>
                      <label className="label">No. WhatsApp*</label>
                      <input className="field" placeholder="08xxxxxxxxxx" value={form.whatsapp} onChange={set("whatsapp")} data-testid="custom-trip-whatsapp-input" />
                    </div>
                  </div>
                  <div>
                    <label className="label">Email (opsional)</label>
                    <input type="email" className="field" placeholder="email@contoh.com" value={form.email} onChange={set("email")} data-testid="custom-trip-email-input" />
                  </div>
                  <div className="rounded-2xl bg-gold-light p-4 text-xs leading-relaxed text-slate-600" data-testid="custom-trip-summary">
                    <p className="font-bold uppercase tracking-wider text-gold-dark">Ringkasan</p>
                    <p className="mt-1">{form.destination || "-"} · {form.arrival || "-"} s/d {form.departure || "-"} · {form.pax || "-"} pax · {form.tripType || "-"}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-8 flex gap-3">
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} className="btn btn-outline !px-5" data-testid="custom-trip-step-prev-button">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            )}
            <button
              onClick={next}
              disabled={sending}
              className="btn btn-primary flex-1"
              data-testid={step === 9 ? "custom-trip-submit-button" : "custom-trip-step-next-button"}
            >
              {step === 9 ? (sending ? "Mengirim..." : "Request My Quotation") : (<>Next <ArrowRight className="h-4 w-4" /></>)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
